/**
 * D3 Force-Directed Graph Visualization
 * Interactive codebase dependency visualization
 */

import type { DependencyGraph, GraphNode, GraphEdge, FileHistory } from '../../core/types';

// D3 will be loaded from CDN
declare const d3: any;

// Color scheme for file groups
const groupColors: Record<string, string> = {
  component: '#00d4ff',
  hook: '#bf5af2',
  utility: '#30d158',
  api: '#ffd60a',
  page: '#ff375f',
  layout: '#64d2ff',
  types: '#98989d',
  config: '#ff9f0a',
  context: '#5e5ce6',
  state: '#ff453a',
  test: '#66d4cf',
  style: '#ff6482',
  contract: '#32d74b',
  module: '#0a84ff',
  docs: '#8e8e93',
  other: '#636366'
};

interface GraphState {
  simulation: any;
  svg: any;
  container: any;
  nodeElements: any;
  linkElements: any;
  selectedNode: GraphNode | null;
  showLabels: boolean;
  sizeMode: 'lines' | 'connections';
  hideOrphans: boolean;
  clusterMode: 'none' | 'folder' | 'type';  // type = by color/group
  zoom: any;
}

let graphState: GraphState | null = null;

/**
 * Load D3 from CDN if not already loaded
 */
async function loadD3(): Promise<void> {
  if (typeof d3 !== 'undefined') return;

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://d3js.org/d3.v7.min.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load D3'));
    document.head.appendChild(script);
  });
}

/**
 * Get node radius based on size mode
 * Uses power scaling for better differentiation between small and large files
 */
function getNodeRadius(node: GraphNode, sizeMode: 'lines' | 'connections'): number {
  const value = sizeMode === 'lines' ? node.lineCount : (node.inDegree + node.outDegree);
  // Power scale: small files get tiny circles, large files get big ones
  // 1 line → ~3px, 50 lines → ~8px, 200 lines → ~14px, 1000 lines → ~25px
  const base = 2 + Math.pow(value, 0.4) * 1.8;
  return Math.max(3, Math.min(40, base));
}

/**
 * Initialize the graph visualization
 */
export async function initGraph(
  container: HTMLElement,
  graph: DependencyGraph,
  fileHistories?: Map<string, FileHistory>,
  onNodeSelect?: (node: GraphNode | null) => void
): Promise<void> {
  await loadD3();

  // Clear previous
  container.textContent = '';

  const width = container.clientWidth;
  const height = container.clientHeight;

  // Create SVG
  const svg = d3.select(container)
    .append('svg')
    .attr('width', '100%')
    .attr('height', '100%')
    .attr('viewBox', [0, 0, width, height]);

  // Add definitions for effects
  const defs = svg.append('defs');

  // Glow filter
  const glow = defs.append('filter')
    .attr('id', 'glow')
    .attr('x', '-50%')
    .attr('y', '-50%')
    .attr('width', '200%')
    .attr('height', '200%');
  glow.append('feGaussianBlur')
    .attr('stdDeviation', '3')
    .attr('result', 'coloredBlur');
  const glowMerge = glow.append('feMerge');
  glowMerge.append('feMergeNode').attr('in', 'coloredBlur');
  glowMerge.append('feMergeNode').attr('in', 'SourceGraphic');

  // Zoom behavior
  const zoom = d3.zoom()
    .scaleExtent([0.1, 4])
    .on('zoom', (event: any) => {
      graphContainer.attr('transform', event.transform);
    });

  svg.call(zoom);

  // Container for graph elements
  const graphContainer = svg.append('g').attr('class', 'graph-container');

  // Process nodes and edges
  const nodes = graph.nodes.map(n => ({ ...n }));
  const edges = graph.edges.map(e => ({
    ...e,
    source: e.source,
    target: e.target
  }));

  // Create node lookup
  const nodeById = new Map<string, GraphNode>();
  nodes.forEach(n => nodeById.set(n.id, n));

  // Folder centers for clustering
  const uniqueFolders = [...new Set(nodes.map(n => n.directory))];
  const folderCenters: Record<string, { x: number; y: number }> = {};
  uniqueFolders.forEach((folder, i) => {
    const angle = (2 * Math.PI * i) / uniqueFolders.length;
    folderCenters[folder] = {
      x: width / 2 + Math.cos(angle) * 300,
      y: height / 2 + Math.sin(angle) * 300
    };
  });

  // Create force simulation
  const simulation = d3.forceSimulation(nodes)
    .force('link', d3.forceLink(edges)
      .id((d: any) => d.id)
      .distance(100)
      .strength(0.5))
    .force('charge', d3.forceManyBody().strength(-200))
    .force('center', d3.forceCenter(width / 2, height / 2))
    .force('collision', d3.forceCollide().radius((d: any) => getNodeRadius(d, 'lines') + 8));

  // Create links
  const linkGroup = graphContainer.append('g').attr('class', 'links');
  const linkElements = linkGroup.selectAll('line')
    .data(edges)
    .join('line')
    .attr('class', 'link')
    .attr('stroke', '#30363d')
    .attr('stroke-opacity', 0.3)
    .attr('stroke-width', 1);

  // Create nodes
  const nodeGroup = graphContainer.append('g').attr('class', 'nodes');
  const nodeElements = nodeGroup.selectAll('g')
    .data(nodes)
    .join('g')
    .attr('class', (d: any) => {
      let cls = 'node';
      if (d.inDegree === 0 && d.outDegree === 0) cls += ' orphan';
      return cls;
    })
    .call(d3.drag()
      .on('start', (event: any, d: any) => {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on('drag', (event: any, d: any) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on('end', (event: any, d: any) => {
        if (!event.active) simulation.alphaTarget(0);
        // Keep node pinned where dropped (fx/fy stay set)
        // Add visual indicator that node is pinned
        d3.select(event.sourceEvent.target.parentNode)
          .classed('pinned', true)
          .select('circle')
          .attr('stroke-dasharray', '3,2');
      }));

  // Double-click to unpin a node
  nodeElements.on('dblclick', (event: any, d: any) => {
    event.stopPropagation();
    d.fx = null;
    d.fy = null;
    d3.select(event.currentTarget)
      .classed('pinned', false)
      .select('circle')
      .attr('stroke-dasharray', null);
    simulation.alpha(0.3).restart();
  });

  // Add circles to nodes
  nodeElements.append('circle')
    .attr('r', (d: any) => getNodeRadius(d, 'lines'))
    .attr('fill', (d: any) => groupColors[d.group] || groupColors.other)
    .attr('stroke', 'rgba(0,0,0,0.7)')
    .attr('stroke-width', 2)
    .attr('cursor', 'pointer');

  // Add labels (hidden by default)
  nodeElements.append('text')
    .text((d: any) => d.name)
    .attr('dy', (d: any) => getNodeRadius(d, 'lines') + 12)
    .attr('text-anchor', 'middle')
    .attr('fill', '#8b949e')
    .attr('font-size', '10px')
    .attr('pointer-events', 'none')
    .attr('opacity', 0);

  // Node click handler
  nodeElements.on('click', (event: any, d: any) => {
    event.stopPropagation();
    selectNode(d);
    if (onNodeSelect) onNodeSelect(d);
  });

  // Background click to deselect
  svg.on('click', () => {
    selectNode(null);
    if (onNodeSelect) onNodeSelect(null);
  });

  // Update positions on tick
  simulation.on('tick', () => {
    linkElements
      .attr('x1', (d: any) => d.source.x)
      .attr('y1', (d: any) => d.source.y)
      .attr('x2', (d: any) => d.target.x)
      .attr('y2', (d: any) => d.target.y);

    nodeElements.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
  });

  // Selection function
  function selectNode(node: GraphNode | null) {
    if (!node) {
      // Deselect all
      nodeElements.classed('selected', false)
        .classed('connected-out', false)
        .classed('connected-in', false)
        .classed('dimmed', false);
      nodeElements.select('circle')
        .attr('stroke', 'rgba(0,0,0,0.7)')
        .attr('stroke-width', 2)
        .attr('filter', null);
      linkElements
        .attr('stroke', '#30363d')
        .attr('stroke-opacity', 0.3)
        .attr('stroke-width', 1);
      graphState!.selectedNode = null;
      return;
    }

    graphState!.selectedNode = node;

    // Find connected nodes
    const outgoing = new Set<string>();
    const incoming = new Set<string>();

    edges.forEach(e => {
      const sourceId = typeof e.source === 'string' ? e.source : (e.source as any).id;
      const targetId = typeof e.target === 'string' ? e.target : (e.target as any).id;

      if (sourceId === node.id) outgoing.add(targetId);
      if (targetId === node.id) incoming.add(sourceId);
    });

    // Update node styles
    nodeElements.each(function(d: any) {
      const el = d3.select(this);
      const circle = el.select('circle');

      if (d.id === node.id) {
        el.classed('selected', true).classed('dimmed', false);
        circle
          .attr('stroke', '#fff')
          .attr('stroke-width', 5)
          .attr('filter', 'url(#glow)');
      } else if (outgoing.has(d.id)) {
        el.classed('connected-out', true).classed('dimmed', false);
        circle
          .attr('stroke', '#30d158')
          .attr('stroke-width', 4)
          .attr('filter', 'url(#glow)');
      } else if (incoming.has(d.id)) {
        el.classed('connected-in', true).classed('dimmed', false);
        circle
          .attr('stroke', '#ff9f0a')
          .attr('stroke-width', 4)
          .attr('filter', 'url(#glow)');
      } else {
        el.classed('dimmed', true);
        circle
          .attr('stroke', 'rgba(0,0,0,0.7)')
          .attr('stroke-width', 2)
          .attr('filter', null);
      }
    });

    // Update link styles
    linkElements.each(function(e: any) {
      const link = d3.select(this);
      const sourceId = typeof e.source === 'string' ? e.source : e.source.id;
      const targetId = typeof e.target === 'string' ? e.target : e.target.id;

      if (sourceId === node.id) {
        link.attr('stroke', '#30d158').attr('stroke-opacity', 0.8).attr('stroke-width', 2);
      } else if (targetId === node.id) {
        link.attr('stroke', '#ff9f0a').attr('stroke-opacity', 0.8).attr('stroke-width', 2);
      } else {
        link.attr('stroke', '#30363d').attr('stroke-opacity', 0.1).attr('stroke-width', 1);
      }
    });
  }

  // Store state
  graphState = {
    simulation,
    svg,
    container: graphContainer,
    nodeElements,
    linkElements,
    selectedNode: null,
    showLabels: false,
    sizeMode: 'lines',
    hideOrphans: false,
    clusterMode: 'none',
    zoom
  };
}

/**
 * Toggle node labels
 */
export function toggleLabels(show: boolean): void {
  if (!graphState) return;
  graphState.showLabels = show;
  graphState.nodeElements.selectAll('text').attr('opacity', show ? 1 : 0);
}

/**
 * Toggle orphan visibility
 */
export function toggleOrphans(hide: boolean): void {
  if (!graphState) return;
  graphState.hideOrphans = hide;
  graphState.nodeElements
    .filter((d: any) => d.inDegree === 0 && d.outDegree === 0)
    .attr('opacity', hide ? 0 : 1);
}

/**
 * Set clustering mode: 'none', 'folder', or 'type' (by color/group)
 */
export function setClusterMode(mode: 'none' | 'folder' | 'type'): void {
  if (!graphState) return;
  graphState.clusterMode = mode;

  const width = graphState.svg.node().clientWidth;
  const height = graphState.svg.node().clientHeight;

  if (mode === 'none') {
    // No clustering - free form
    graphState.simulation
      .force('x', null)
      .force('y', null)
      .force('collision', d3.forceCollide()
        .radius((d: any) => getNodeRadius(d, graphState!.sizeMode) + 5))
      .force('charge', d3.forceManyBody().strength(-150));
  } else {
    // Get unique groups for clustering
    const nodes = graphState.nodeElements.data();
    const uniqueGroups = mode === 'folder'
      ? [...new Set(nodes.map((n: GraphNode) => n.directory))]
      : [...new Set(nodes.map((n: GraphNode) => n.group))];

    const groupCenters: Record<string, { x: number; y: number }> = {};

    // Spread clusters in a circle, further apart for more groups
    const radius = Math.min(500, 200 + uniqueGroups.length * 25);

    uniqueGroups.forEach((group: string, i: number) => {
      const angle = (2 * Math.PI * i) / uniqueGroups.length - Math.PI / 2; // Start at top
      groupCenters[group] = {
        x: width / 2 + Math.cos(angle) * radius,
        y: height / 2 + Math.sin(angle) * radius
      };
    });

    // Get the grouping key based on mode
    const getGroupKey = (d: any) => mode === 'folder' ? d.directory : d.group;

    // Apply clustering forces - gentle pull toward group center
    graphState.simulation
      .force('x', d3.forceX((d: any) => groupCenters[getGroupKey(d)]?.x || width / 2).strength(0.08))
      .force('y', d3.forceY((d: any) => groupCenters[getGroupKey(d)]?.y || height / 2).strength(0.08))
      // Strong collision to prevent overlap
      .force('collision', d3.forceCollide()
        .radius((d: any) => getNodeRadius(d, graphState!.sizeMode) + 12)
        .strength(0.95)
        .iterations(4))
      // Strong charge to push nodes apart within clusters
      .force('charge', d3.forceManyBody().strength(-250));
  }

  graphState.simulation.alpha(0.6).restart();
}

/**
 * Toggle folder clustering (legacy - wraps setClusterMode)
 */
export function toggleClustering(cluster: boolean): void {
  setClusterMode(cluster ? 'folder' : 'none');
}

/**
 * Set node size mode
 */
export function setSizeMode(mode: 'lines' | 'connections'): void {
  if (!graphState) return;
  graphState.sizeMode = mode;

  graphState.nodeElements.selectAll('circle')
    .transition()
    .duration(300)
    .attr('r', (d: any) => getNodeRadius(d, mode));

  graphState.nodeElements.selectAll('text')
    .transition()
    .duration(300)
    .attr('dy', (d: any) => getNodeRadius(d, mode) + 12);

  // Update collision force
  graphState.simulation
    .force('collision', d3.forceCollide().radius((d: any) => getNodeRadius(d, mode) + 8))
    .alpha(0.3)
    .restart();
}

/**
 * Zoom to fit all nodes
 */
export function zoomToFit(): void {
  if (!graphState) return;

  const bounds = graphState.container.node().getBBox();
  const width = graphState.svg.node().clientWidth;
  const height = graphState.svg.node().clientHeight;

  const scale = 0.9 * Math.min(width / bounds.width, height / bounds.height);
  const tx = width / 2 - scale * (bounds.x + bounds.width / 2);
  const ty = height / 2 - scale * (bounds.y + bounds.height / 2);

  graphState.svg.transition()
    .duration(500)
    .call(graphState.zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(scale));
}

/**
 * Search and highlight nodes
 */
export function searchNodes(query: string): GraphNode[] {
  if (!graphState) return [];

  const lowerQuery = query.toLowerCase();
  const matches: GraphNode[] = [];

  graphState.nodeElements.each(function(d: any) {
    const el = d3.select(this);
    const match = d.name.toLowerCase().includes(lowerQuery) ||
      d.id.toLowerCase().includes(lowerQuery);

    if (match && query) {
      matches.push(d);
      el.attr('opacity', 1);
    } else if (query) {
      el.attr('opacity', 0.2);
    } else {
      el.attr('opacity', 1);
    }
  });

  return matches;
}

/**
 * Unpin all nodes - release all fixed positions
 */
export function unpinAll(): void {
  if (!graphState) return;

  graphState.nodeElements.each(function(d: any) {
    d.fx = null;
    d.fy = null;
    d3.select(this)
      .classed('pinned', false)
      .select('circle')
      .attr('stroke-dasharray', null);
  });

  graphState.simulation.alpha(0.5).restart();
}

/**
 * Export graph as SVG string
 */
export function exportToSVG(): string | null {
  if (!graphState) return null;

  // Clone the SVG to avoid modifying the original
  const svgElement = graphState.svg.node();
  const clone = svgElement.cloneNode(true) as SVGElement;

  // Add styles inline for proper rendering
  const styleSheet = `
    <style>
      .node circle { cursor: pointer; }
      .link { stroke: #30363d; stroke-opacity: 0.4; fill: none; }
      .label { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 10px; fill: #e6edf3; pointer-events: none; }
      .node.highlighted circle { stroke-width: 3; }
      .node.dimmed { opacity: 0.2; }
      .link.highlighted { stroke-opacity: 0.8; }
      .link.dimmed { stroke-opacity: 0.05; }
    </style>
  `;

  // Insert styles at the beginning
  clone.insertAdjacentHTML('afterbegin', styleSheet);

  // Set explicit dimensions
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

  // Add background
  const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  bg.setAttribute('width', '100%');
  bg.setAttribute('height', '100%');
  bg.setAttribute('fill', '#0d1117');
  clone.insertBefore(bg, clone.firstChild);

  return new XMLSerializer().serializeToString(clone);
}

/**
 * Export graph as PNG blob
 */
export async function exportToPNG(scale: number = 2): Promise<Blob | null> {
  if (!graphState) return null;

  const svgString = exportToSVG();
  if (!svgString) return null;

  const svgElement = graphState.svg.node();
  const bbox = svgElement.getBoundingClientRect();
  const width = bbox.width * scale;
  const height = bbox.height * scale;

  return new Promise((resolve) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = width;
    canvas.height = height;

    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      if (ctx) {
        ctx.fillStyle = '#0d1117';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
      }
      URL.revokeObjectURL(url);

      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/png');
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };

    img.src = url;
  });
}

/**
 * Cleanup graph
 */
export function destroyGraph(): void {
  if (!graphState) return;
  graphState.simulation.stop();
  graphState = null;
}

/**
 * Get current node positions (including pinned fx/fy)
 */
export function getNodePositions(): Record<string, { x: number; y: number; fx: number | null; fy: number | null }> | null {
  if (!graphState) return null;
  const positions: Record<string, { x: number; y: number; fx: number | null; fy: number | null }> = {};
  graphState.nodeElements.each((d: any) => {
    positions[d.id] = {
      x: d.x,
      y: d.y,
      fx: d.fx ?? null,
      fy: d.fy ?? null
    };
  });
  return positions;
}

/**
 * Apply saved node positions
 */
export function applyNodePositions(positions: Record<string, { x: number; y: number; fx: number | null; fy: number | null }>): void {
  if (!graphState) return;

  graphState.nodeElements.each(function(d: any) {
    const saved = positions[d.id];
    if (saved) {
      d.x = saved.x;
      d.y = saved.y;
      if (saved.fx !== null && saved.fy !== null) {
        d.fx = saved.fx;
        d.fy = saved.fy;
        // Add visual indicator for pinned nodes
        d3.select(this)
          .classed('pinned', true)
          .select('circle')
          .attr('stroke-dasharray', '3,2');
      }
    }
  });

  // Update positions immediately without restarting simulation
  graphState.linkElements
    .attr('x1', (d: any) => d.source.x)
    .attr('y1', (d: any) => d.source.y)
    .attr('x2', (d: any) => d.target.x)
    .attr('y2', (d: any) => d.target.y);

  graphState.nodeElements.attr('transform', (d: any) => `translate(${d.x},${d.y})`);

  // Gentle simulation restart to settle unpinned nodes
  graphState.simulation.alpha(0.1).restart();
}

export { groupColors };
